import React, { FC, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { message } from 'antd';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { PROFILE_LIST_PAGE } from '../../../../common/constants/routes';
import { determineIsProxyTruthy, determineIsTorOrFreeProxy } from '../../../../common/constants/types';

import { GROUPED_PROXY_MANAGER_ANALYTICS_EVENTS } from '../../../../common/constants/analytics';
import { IProxy } from '../../../interfaces';
import PerformanceObserverService from '../../../services/performance-observer/performance-observer.service';
import { templatesContext } from '../../../state';
import { NEW_FEATURES } from '../../../state/feature-toggle/new-features';
import { getHintPreferences } from '../../../state/hint-preferences/hint-preferences.atom';
import { openLocationChangedHint } from '../../../state/opened-location-changed.atom';
import { getDefaultGeoProxy, updateGeoProxyCustomNameCounters } from '../../../state/proxy/default-geoproxy.atom';
import { useGeoProxyCountries } from '../../../state/proxy/geoproxy-countries.atom';
import { PROXY_CHECK_TOOLTIP_LOCATIONS } from '../../../state/proxy/proxy-check/constants';
import { showProxyCheckTooltip } from '../../../state/proxy/proxy-check/proxy-check-tooltip.atom';
import { addProxyStatuses, removeProxyStatuses } from '../../../state/proxy/proxy-check/proxy-statuses.atom';
import { getProxyList, pushManyProxies, updateProxyInListById } from '../../../state/proxy/proxy-list.atom';
import { closeProxyManager, getProxyManagerState } from '../../../state/proxy/proxy-manager-modal-status.atom';
import {
  checkGeoProxy,
  evaluateSelectableGeoProxyConnectionType,
  fetchGeoProxyWithCredentials,
  GeoProxyWithCredentialsParams,
  handleGeoProxyWithCredentialsError,
} from '../../../state/proxy/proxy-operations/create-geoproxy.operations';
import {
  decrementProfileLinkedProxyProfilesCounter,
  handleLinkArtificialProxy,
  linkProfileProxy,
  linkProfileProxyInState,
} from '../../../state/proxy/proxy-operations/link-proxy.operations';
import { openProxySettingsModal } from '../../../state/proxy/proxy-settings-modal.atom';
import { updateRecentSelectedCountriesAtom } from '../../../state/proxy/recent-selected-proxies.atom';
import { useTrafficData } from '../../../state/proxy/traffic-data.atom';
import { IconPlus } from '../../../ui/gologin-header/icons';
import IconPlusInCircle from '../../../ui/icons/IconPlusInCircle';
import { sendActionAnalytics } from '../../common/api';
import { PROXY_OBSERVED_USER_ACTIONS } from '../../proxy/constants';
import {
  generateProxyAnalyticsData,
  getIsProxyArchived,
  makeGeoProxyCustomName,
  restoreProxy,
} from '../../proxy/proxy-helpers';
import connectionType from '../../proxy/proxy-page/proxy-table/cells/connection-type';
import { evaluateGeoProxyCustomNameCounter, readStoredGeoProxyCity, readStoredGeoProxyCityWithoutDefault } from '../../proxy/utils/default-proxy-data';
import { generateArtificialGeoProxyId, generateProxyGroupId, getNoIdProxyId } from '../../proxy/utils/proxy-id';
import { quickCreateProfile } from './create-quick-profile.action';
import LoadableIconButton from './loadable-icon-button';

interface IQuickCreateProfile {
  tooltipContent: JSX.Element;
}

const makeArtificialGeoProxy = ({ country, city, connectionType, customName, profilesCount, groupId }: any): IProxy => {
  const proxyId = generateArtificialGeoProxyId();
  updateGeoProxyCustomNameCounters(country);

  return {
    id: proxyId,
    groupId,
    host: '',
    port: 80,
    country,
    city,
    connectionType,
    customName,
    profilesCount,
    customSortingInGroup: 'end',
    createdAt: new Date(),
    selectionDate: profilesCount ? Date.now() : 0,
  };
};

const ButtonQuickCreateProfile: FC<IQuickCreateProfile> = props => {
  const { tooltipContent } = props;

  const geoProxyCountries = useGeoProxyCountries();
  const trafficData = useTrafficData();

  const templateCtx = useContext(templatesContext);

  const location = useLocation();
  const isProfilesPage = location.pathname === PROFILE_LIST_PAGE;

  const renderIcon = (): JSX.Element => {
    if (NEW_FEATURES.header) {
      return (
        <IconPlus
          margin='-4px 0 0 0'
          styleType='activeGrayWithBackground'
          iconColor='var(--98989F-profile-table-create-quick-profile)'
          iconHoveredColor='var(--2B2B31-profile-table-create-quick-profile)'
        />
      );
    }

    return <IconPlusInCircle />;
  };

  const connectingLastUsedGeoproxy = async (proxyEntity: IProxy, currentProfileId: any): Promise<void> => {
    const isArtificialProxy = handleLinkArtificialProxy(proxyEntity, 'proxy-item');
    const isTorOrFreeProxy = determineIsTorOrFreeProxy(proxyEntity);
    const { handleProxySelect } = getProxyManagerState();

    if (isArtificialProxy && !isTorOrFreeProxy) {
      return;
    }

    if (!proxyEntity.id) {
      // const groupId = generateProxyGroupId('geolocation', proxyEntity.country);
      // const currentCountryWithTypes = geoProxyCountries.find(
      //   ({ countryCode }) => countryCode.toLowerCase() === proxyEntity.country.toLowerCase(),
      // );
      // const availableTypes = currentCountryWithTypes ? currentCountryWithTypes.types : [];
      // const selectableConnectionType = evaluateSelectableGeoProxyConnectionType({
      //   country: proxyEntity.country,
      //   groupId,
      //   selectedConnectionType: connectionType,
      //   availableConnectionTypes: availableTypes,
      //   trafficData,
      // });

      // if (!selectableConnectionType) {
      //   return;
      // }

      // const profilesCount = currentProfileId && !handleProxySelect ? 1 : 0;
      // const artificialGeoProxy = makeArtificialGeoProxy({
      //   country: proxyEntity.country,
      //   city: proxyEntity.city,
      //   customName: proxyEntity.customName,
      //   groupId,
      //   profilesCount,
      // });

      // const geoProxyWithCredentialsParams: GeoProxyWithCredentialsParams = {
      //   country: proxyEntity.country,
      //   city: proxyEntity.city,
      //   connectionType: proxyEntity.connectionType,
      //   profileId: '',
      //   customName: proxyEntity.customName,
      // };

      // closeProxyManager();

      // addProxyStatuses([artificialGeoProxy], [currentProfileId]);
      // pushManyProxies([artificialGeoProxy]);
      // if (handleProxySelect) {
      //   let proxyId = artificialGeoProxy.id;
      //   if (!proxyId && determineIsProxyTruthy(artificialGeoProxy)) {
      //     proxyId = getNoIdProxyId(artificialGeoProxy);
      //   }

      //   handleProxySelect(proxyId);
      // } else {
      //   decrementProfileLinkedProxyProfilesCounter(currentProfileId);
      //   linkProfileProxyInState({
      //     profileId: currentProfileId,
      //     proxy: artificialGeoProxy,
      //     shouldUpdateProxyInList: false,
      //   });

      //   geoProxyWithCredentialsParams.profileIdToLink = currentProfileId;
      // }

      // const geoProxyWithCredentialsResponse = await fetchGeoProxyWithCredentials(geoProxyWithCredentialsParams).catch(
      //   error => {
      //     handleGeoProxyWithCredentialsError(artificialGeoProxy, currentProfileId);
      //     let errorMessage = 'tableProfiles.notification.trafficLimit';
      //     if (typeof error?.body?.message === 'string') {
      //       errorMessage = error.body.message;
      //     }

      //     return errorMessage;
      //   },
      // );

      // if (typeof geoProxyWithCredentialsResponse === 'string') {
      //   return message.error(<Trans i18nKey={geoProxyWithCredentialsResponse} />);
      // }

      // const proxyWithCredentials = geoProxyWithCredentialsResponse;
      // const fullProxy: IProxy = {
      //   ...artificialGeoProxy,
      //   ...proxyWithCredentials,
      //   mode: 'geolocation',
      // };

      // const checkResult = await checkGeoProxy(
      //   fullProxy,
      //   currentProfileId,
      //   PROXY_CHECK_TOOLTIP_LOCATIONS.proxyManagerEditForm,
      // );
      // console.log(fullProxy, checkResult);

      // if (typeof checkResult === 'string') {
      //   handleGeoProxyWithCredentialsError(artificialGeoProxy, currentProfileId);

      //   return message.error(<Trans i18nKey={checkResult} />);
      // }

      // const checkedProxy = checkResult;

      // console.log(artificialGeoProxy, checkedProxy);
      // updateProxyInListById(artificialGeoProxy.id, checkedProxy);
      // removeProxyStatuses([checkedProxy], [currentProfileId]);

      // const fullCheckedProxy: IProxy = { ...checkedProxy, checkDate: moment().toDate() };

      // showProxyCheckTooltip({
      //   profileIds: [currentProfileId],
      //   proxies: [fullCheckedProxy],
      //   view: PROXY_CHECK_TOOLTIP_LOCATIONS.selectorProfileTable,
      //   timeout: 2000,
      // });

      const groupId = generateProxyGroupId('geolocation', proxyEntity.country);
      const currentCountryWithTypes = geoProxyCountries.find(
        ({ countryCode }) => countryCode.toLowerCase() === proxyEntity.country.toLowerCase(),
      );
      const availableTypes = currentCountryWithTypes ? currentCountryWithTypes.types : [];
      const selectableConnectionType = evaluateSelectableGeoProxyConnectionType({
        country: proxyEntity.country,
        groupId,
        selectedConnectionType: connectionType,
        availableConnectionTypes: availableTypes,
        trafficData,
      });

      if (!selectableConnectionType) {
        return;
      }

      const profilesCount = currentProfileId && !handleProxySelect ? 1 : 0;
      const artificialGeoProxy = makeArtificialGeoProxy({
        country: proxyEntity.country,
        city: proxyEntity.city,
        customName: proxyEntity.customName,
        groupId,
        profilesCount,
      });

      const geoProxyWithCredentialsParams: GeoProxyWithCredentialsParams = {
        country: proxyEntity.country,
        city: proxyEntity.city,
        connectionType: selectableConnectionType,
        profileId: '',
        customName: proxyEntity.customName,
      };

      closeProxyManager();

      addProxyStatuses([artificialGeoProxy], [currentProfileId]);
      pushManyProxies([artificialGeoProxy]);
      if (handleProxySelect) {
        let proxyId = artificialGeoProxy.id;
        if (!proxyId && determineIsProxyTruthy(artificialGeoProxy)) {
          proxyId = getNoIdProxyId(artificialGeoProxy);
        }

        handleProxySelect(proxyId);
      } else {
        decrementProfileLinkedProxyProfilesCounter(currentProfileId);
        linkProfileProxyInState({
          profileId: currentProfileId,
          proxy: artificialGeoProxy,
          shouldUpdateProxyInList: false,
        });

        geoProxyWithCredentialsParams.profileIdToLink = currentProfileId;
      }

      const geoProxyWithCredentialsResponse = await fetchGeoProxyWithCredentials(geoProxyWithCredentialsParams).catch(
        error => {
          handleGeoProxyWithCredentialsError(artificialGeoProxy, currentProfileId);
          let errorMessage = 'tableProfiles.notification.trafficLimit';
          if (typeof error?.body?.message === 'string') {
            errorMessage = error.body.message;
          }

          return errorMessage;
        },
      );

      if (typeof geoProxyWithCredentialsResponse === 'string') {
        return message.error(<Trans i18nKey={geoProxyWithCredentialsResponse} />);
      }

      const proxyWithCredentials = geoProxyWithCredentialsResponse;
      const fullProxy: IProxy = {
        ...artificialGeoProxy,
        ...proxyWithCredentials,
        mode: 'geolocation',
      };

      const checkResult = await checkGeoProxy(
        fullProxy,
        currentProfileId,
        PROXY_CHECK_TOOLTIP_LOCATIONS.proxyManagerEditForm,
      );

      if (typeof checkResult === 'string') {
        handleGeoProxyWithCredentialsError(artificialGeoProxy, currentProfileId);

        return message.error(<Trans i18nKey={checkResult} />);
      }

      const checkedProxy = checkResult;
      updateProxyInListById(artificialGeoProxy.id, checkedProxy);
      removeProxyStatuses([artificialGeoProxy], [currentProfileId]);

      const fullCheckedProxy: IProxy = { ...checkedProxy, checkDate: moment().toDate() };
      showProxyCheckTooltip({
        profileIds: [currentProfileId],
        proxies: [fullCheckedProxy],
        view: PROXY_CHECK_TOOLTIP_LOCATIONS.selectorProfileTable,
        timeout: 2000,
      });

      if (handleProxySelect) {
        let proxyId = checkedProxy.id;
        if (!proxyId && determineIsProxyTruthy(checkedProxy)) {
          proxyId = getNoIdProxyId(checkedProxy);
        }

        handleProxySelect(proxyId);
      } else {
        linkProfileProxyInState({
          profileId: currentProfileId,
          proxy: checkedProxy,
          shouldUpdateProxyInList: false,
        });
      }

      return;
    }

    let proxyToLink = proxyEntity;

    if (getIsProxyArchived(proxyEntity)) {
      proxyToLink = await restoreProxy(proxyEntity);
    }

    if (NEW_FEATURES.proxyGroupsV2 && proxyEntity?.groupId) {
      const proxyAnalyticsData = generateProxyAnalyticsData(proxyToLink);
      sendActionAnalytics(
        GROUPED_PROXY_MANAGER_ANALYTICS_EVENTS.choseLocationViaBuiltInProxyClickedOnServerTypeInExpandedGroup,
        proxyAnalyticsData,
      ).catch(() => null);
    }

    if (handleProxySelect) {
      let proxyId = proxyEntity.id;
      if (!proxyId && determineIsProxyTruthy(proxyToLink)) {
        proxyId = getNoIdProxyId(proxyToLink);
      }

      handleProxySelect(proxyId);

      return;
    }

    if (currentProfileId) {
      const performanceObserverService = PerformanceObserverService.getInstance();
      performanceObserverService.handleUserAction({ userAction: PROXY_OBSERVED_USER_ACTIONS.linkProxyManagerProxy });

      closeProxyManager();
      linkProfileProxy({
        profileId: currentProfileId,
        proxy: proxyToLink,
      });
    }
    if (proxyToLink.country) {
      updateRecentSelectedCountriesAtom({
        countryCode: proxyToLink.country,
      });
    }
  };

  const handleClick = async (): Promise<void> => {
    if (isProfilesPage) {
      const newProfileData = await quickCreateProfile(templateCtx);
      const proxyList = getProxyList();
      const defaultGeoProxy =
        proxyList
          .filter(proxy => proxy.checkDate && proxy.mode === 'geolocation')
          .sort((a, b) => {
            const dateA = a.checkDate instanceof Date ? a.checkDate : new Date(a.checkDate);
            const dateB = b.checkDate instanceof Date ? b.checkDate : new Date(b.checkDate);
            return dateB.getTime() - dateA.getTime();
          })[0] || getDefaultGeoProxy();

      const { counter } = evaluateGeoProxyCustomNameCounter(defaultGeoProxy.country);
      const cityValue = readStoredGeoProxyCityWithoutDefault(defaultGeoProxy.country);
      const city = cityValue === 'auto' ? '' : cityValue || '';
          console.log(cityValue, 'cityValue')
      const customName = makeGeoProxyCustomName({
        countryCode: defaultGeoProxy.country,
        city: city,
        counter,
      });

      const newProxy = {
        groupId: null,
        customName: customName,
        mode: 'geolocation',
        country: defaultGeoProxy.country,
        city: city,
        connectionType: defaultGeoProxy.connectionType,
        selectionDate: Date.now(),
      };

      if (templateCtx.selectedTemplate.proxy.quickProfileCreate === 'last-used-geoproxy') {
        connectingLastUsedGeoproxy(newProxy, newProfileData.id);

        const { locationChanged } = getHintPreferences();
        if (locationChanged !== 'hidden') {
          openLocationChangedHint(newProfileData.id);
        }
      }
    } else {
      openProxySettingsModal({ modalView: 'proxy-add', currentProxy: null });
    }
  };

  return (
    <LoadableIconButton
      onClick={handleClick}
      icon={renderIcon()}
      tooltipContent={tooltipContent}
      style={{
        margin: NEW_FEATURES.header ? '-4px 5px 0 0' : '-4px 5px 0 11px',
        height: 21,
      }}
    />
  );
};

export default ButtonQuickCreateProfile;
